import { useStaticQuery, graphql, Link } from 'gatsby'
import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import styled from 'styled-components'
import Plaatjie from '@ubo/plaatjie'

import Title from 'components/elements/Title'
import {
  Carousel,
  CarouselControls,
  CarouselContent,
  CarouselItem,
} from 'components/elements/Carousel'
import ButtonOutline from 'components/elements/ButtonOutline'

const ProjectSlide = styled(CarouselItem)`
  flex: 0 0 80% !important;
  max-width: 100%;
  padding-left: 0.5rem;
  padding-right: 0.5rem;

  @media (min-width: 992px) and (max-width: 1199px) {
    flex: 0 0 45% !important;
    max-width: 45%;
  }

  @media (min-width: 1199px) {
    flex: 0 0 31.333% !important;
    max-width: 31.333%;
  }
`

const SlideReveal = styled(motion.div)`
  width: 100%;
`

const SlideImage = styled(Plaatjie)`
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;

  & .gatsby-image-wrapper,
  & picture,
  & img {
    width: 100% !important;
    height: 100% !important;
  }

  & img {
    object-fit: cover;
  }
`

const SlidePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${(props) => props.theme.color.face.mainDark};
`

const SlideGradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40%;
  background: linear-gradient(
    to top,
    rgba(55, 18, 22, 0.85) 0%,
    rgba(55, 18, 22, 0.35) 45%,
    rgba(0, 0, 0, 0) 100%
  );
  pointer-events: none;
  z-index: 1;
`

const SlideCaption = styled.span`
  position: absolute;
  bottom: 52px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['20']};
  font-weight: 300;
  color: ${(props) => props.theme.color.text.light};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  z-index: 2;
  pointer-events: none;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  transition: bottom 0.28s ease;

  @media (max-width: 767px) {
    font-size: ${(props) => props.theme.font.size['18']};
  }
`

const SlideLink = styled(Link)`
  display: block;
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 3 / 4;
  min-height: 280px;
  text-decoration: none;
  color: inherit;

  &:hover,
  &:focus-visible {
    text-decoration: none;
    color: inherit;
  }

  &:hover ${SlideCaption}, &:focus-visible ${SlideCaption} {
    bottom: 76px;
  }

  &:hover .slide-hover-extras,
  &:focus-visible .slide-hover-extras {
    opacity: 1;
  }
`

const SlideHoverExtras = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.28s ease;
`

const SlideBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  background-color: ${(props) => props.theme.color.face.mainLight};
  color: ${(props) => props.theme.color.face.contrast};
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['14']};
  font-weight: 600;
  line-height: 1.2;
`

const SlideCta = styled.span`
  position: absolute;
  bottom: 26px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-block;
  padding: 0.45rem 1.15rem;
  white-space: nowrap;
  border: 1px solid ${(props) => props.theme.color.text.light};
  border-radius: 4px;
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['14']};
  font-weight: 600;
  color: ${(props) => props.theme.color.text.light};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

interface ProjectSliderProps {
  fields: {
    title?: string | null
    subtitle?: string | null
    readMoreText?: string | null
  }
}

type ProjectNode = {
  uri?: string | null
  title?: string | null
  projectCategories?: {
    nodes?: Array<{ name?: string | null } | null> | null
  } | null
  projectDetail?: {
    thumbnail?: {
      localFile?: {
        childImageSharp?: { gatsbyImageData: unknown }
      }
    } | null
  } | null
}

const DEFAULT_CTA = 'BEKIJK PROJECT →'
const SLIDE_STAGGER_S = 0.14

type ProjectSlideInnerProps = {
  prefersReducedMotion: boolean | null
  node: ProjectNode
  ctaLabel: string
  index: number
}

function ProjectSlideInner({
  prefersReducedMotion,
  node,
  ctaLabel,
  index,
}: ProjectSlideInnerProps) {
  const thumbnail = node.projectDetail?.thumbnail
  const categoryName = node.projectCategories?.nodes?.[0]?.name || null

  const link = (
    <SlideLink to={node.uri || '/'}>
      {thumbnail ? (
        <SlideImage image={thumbnail} alt={node.title || ''} loading="lazy" />
      ) : (
        <SlidePlaceholder aria-hidden />
      )}
      <SlideGradient aria-hidden />
      <SlideCaption>{node.title}</SlideCaption>
      <SlideHoverExtras className="slide-hover-extras">
        {categoryName && <SlideBadge>{categoryName}</SlideBadge>}
        <SlideCta>{ctaLabel}</SlideCta>
      </SlideHoverExtras>
    </SlideLink>
  )

  if (prefersReducedMotion) {
    return link
  }

  return (
    <SlideReveal
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15, margin: '0px 0px -24px 0px' }}
      transition={{
        duration: 0.48,
        delay: index * SLIDE_STAGGER_S,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {link}
    </SlideReveal>
  )
}

function ProjectSlider({ fields }: ProjectSliderProps) {
  const prefersReducedMotion = useReducedMotion()
  const data = useStaticQuery<GatsbyTypes.projectSliderQueryQuery>(graphql`
    query projectSliderQuery {
      fields: allWpProject(limit: 5, sort: { order: DESC, fields: date }) {
        edges {
          node {
            uri
            title
            projectCategories {
              nodes {
                name
              }
            }
            projectDetail {
              fieldGroupName
              thumbnail {
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 800, quality: 90)
                  }
                }
              }
            }
          }
        }
      }
    }
  `)

  const edges = data.fields?.edges ?? []
  const ctaLabel = fields.readMoreText?.trim() || DEFAULT_CTA

  return (
    <section>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Carousel className="mt-4" opts={{ align: 'start', loop: false }}>
              <div className="d-flex justify-content-between align-items-center mb-5">
                <div>
                  {fields.title && (
                    <Title className="mb-2">{fields.title}</Title>
                  )}
                  <CarouselControls />
                </div>

                <ButtonOutline to="/portfolio/">
                  BEKIJK ALLE PROJECTEN
                </ButtonOutline>
              </div>
              <CarouselContent>
                {edges.map((edge: { node: ProjectNode }, index: number) => {
                  const { node } = edge

                  return (
                    <ProjectSlide key={node.uri || node.title}>
                      <ProjectSlideInner
                        prefersReducedMotion={prefersReducedMotion}
                        node={node}
                        ctaLabel={ctaLabel}
                        index={index}
                      />
                    </ProjectSlide>
                  )
                })}
              </CarouselContent>
            </Carousel>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProjectSlider
